import {MapLayerTooltipFunction}               from '../../ap-map/layers/map-factory-layer';
import {Injectable}                            from '@angular/core';
import {GetRoundNumericService}                from '../../ap-utils/service/get-round-numeric.service';
import {ISoilGroupFeature, ISoilSampleFeature} from './interfaces/soil-sample-feature.interface';
import * as moment                             from 'moment-timezone';
import {ApElementOxydTypeEnum}                 from '../../ap-interface';
import {ApElementType}                         from '../../ap-interface/enums/ap-elements-type.enum';
import {AgriportConstantsService}              from '../../services/common/agriport-constants.service';
import {LanguageStore}                         from '../translation/language.store';
import {GetElementService}                     from '../../ap-utils/service/ap-get-element.service';
import {ApTranslationService}                  from '../../ap-utils/service/ap-translation.service';
import {ILegend}                               from './map.legend.store';

@Injectable({providedIn: 'root'})
export class MapLayerTooltipStore {
  constructor(private languageStore: LanguageStore,
              private elementService: GetElementService,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private agriportConstantsService: AgriportConstantsService) {
  }

  public getNutrientPlanningsLayerTooltip(): MapLayerTooltipFunction {
    return (featureProperties: { [key: string]: any }, legend: ILegend) => {
      if (!featureProperties) {
        return undefined;
      }
      const rawValue = +JSON.parse(featureProperties.data)?.value;
      let value = this.roundNumericService.roundValue(rawValue);
      const selectedLegendValue = legend.selectionLists[0]?.selectedValue.getValue();
      if (!value || value?.length <= 0) {
        return undefined;
      }
      if (selectedLegendValue.transformFunction) {
        value = this.roundNumericService.round(selectedLegendValue.transformFunction(rawValue), 0);
      }
      return `${selectedLegendValue.text}: ${value} ${selectedLegendValue.suffix ?? ''}`;
    };
  }

  public getSoilSamplesLayerTooltip(): MapLayerTooltipFunction {
    return (featureProperties: { [key: string]: any }, _: ILegend) => {
      if (!featureProperties) {
        return undefined;
      }
      const soilSampleFeature: ISoilSampleFeature = JSON.parse(featureProperties.data);
      const sampleDate = moment(soilSampleFeature?.sampledate);
      let sampleDateFormatted = '-';
      if (sampleDate && sampleDate.isValid()) {
        sampleDateFormatted = sampleDate.format(this.languageStore.SelectedLanguage.DateFormat);
      }
      const pValue = soilSampleFeature ? this.elementService.convertElementOxid(soilSampleFeature?.p ?? 0, ApElementOxydTypeEnum.P, soilSampleFeature?.method_p) : 0;
      const kValue = soilSampleFeature ? this.elementService.convertElementOxid(soilSampleFeature?.k ?? 0, ApElementOxydTypeEnum.K, soilSampleFeature?.method_k) : 0;
      const mgValue = soilSampleFeature ? this.elementService.convertElementOxid(soilSampleFeature?.mg ?? 0, ApElementOxydTypeEnum.Mg, soilSampleFeature?.method_mg) : 0;
      const tooltipLines = [
        `${this.translationService.translate('Global__SampleDate')}: ${sampleDateFormatted}`,
        `${this.translationService.translate('GPS_Number')}: ${soilSampleFeature?.gpsnumber ?? '-'}`,
        `${this.elementService.GetElementStringByString(ApElementType.P)}: ${pValue} [${soilSampleFeature?.class_p ?? '-'}]`,
        `${this.elementService.GetElementStringByString(ApElementType.K)}: ${kValue} [${soilSampleFeature?.class_k ?? '-'}]`,
        `${this.elementService.GetElementStringByString(ApElementType.Mg)}: ${mgValue} [${soilSampleFeature?.class_mg ?? '-'}]`,
        `${this.translationService.translate('Global__Nutrient_pH_Short')}: ${soilSampleFeature?.ph ?? 0} [${soilSampleFeature?.class_ph ?? '-'}]`
      ];
      return tooltipLines.join('\n');
    };
  }

  public getSoilGroupMapLayerTooltip(): MapLayerTooltipFunction {
    return (featureProperties: { [key: string]: any }, _: ILegend) => {
      if (!featureProperties) {
        return undefined;
      }
      const soilGroupFeature: ISoilGroupFeature = JSON.parse(featureProperties.data);
      return `${this.translationService.translate('Nutrients__Soilgroup')}: ${soilGroupFeature?.group ?? '-'}`;
    };
  }

  public getNeedLayerTooltip(): MapLayerTooltipFunction {
    return (featureProperties: { [key: string]: any }, legend: ILegend) => {
      if (!featureProperties) {
        return undefined;
      }
      let rawValue = +JSON.parse(featureProperties.data)?.value;
      const mapLegendTypeElement = legend.selectionLists[1]?.selectedValue.getValue();
      if (!isFinite(rawValue) || !mapLegendTypeElement?.text) {
        return undefined;
      }
      if (mapLegendTypeElement.transformFunction) {
        rawValue = mapLegendTypeElement.transformFunction(rawValue);
      }
      const value = this.roundNumericService.roundValue(rawValue);
      return `${mapLegendTypeElement.text}: ${value} ${legend.unit}`;
    };
  }

  public getSensorPointsLayerTooltip(): MapLayerTooltipFunction {
    return (featureProperties: { [key: string]: any }, _: ILegend) => {
      if (!featureProperties) {
        return undefined;
      }
      const value = this.roundNumericService.round(+JSON.parse(featureProperties.data)?.value, 0);
      if (!value || value?.length <= 0) {
        return undefined;
      }
      const logPoints = this.translationService.translate('Global__SamplePoints');
      const nUptake = this.translationService.translate('NUptake');
      const unit = this.translationService.translate('Global__Unit_kgN_ha');
      return `${logPoints} ${nUptake}: ${value} ${unit}`;
    };
  }

  public getNRasterLayerTooltip(): MapLayerTooltipFunction {
    return (featureProperties: { [key: string]: any }, legend: ILegend) => {
      if (!featureProperties) {
        return undefined;
      }
      const rawValue = +JSON.parse(featureProperties.data)?.value;
      if (!isFinite(rawValue)) {
        return undefined;
      }
      const selectedLegendValue = legend.selectionLists[0]?.selectedValue.getValue();
      const text = this.translationService.translate(selectedLegendValue.text ?? '');
      if (rawValue === +this.agriportConstantsService.GetConstant('RasterNoValue')) {
        return `${text}: -`;
      }
      const value = this.roundNumericService.roundValue(rawValue);
      const suffix = this.translationService.translate(selectedLegendValue.suffix ?? '');
      return `${text}: ${value} ${suffix}`;
    };
  }

  public getNdiLayerTooltip(): MapLayerTooltipFunction {
    return (featureProperties: { [key: string]: any }, _: ILegend) => {
      if (!featureProperties) {
        return undefined;
      }
      const tooltipLines = [];
      const nIndex = JSON.parse(featureProperties.data)?.ndi;
      if (nIndex) {
        tooltipLines.push(`${this.translationService.translate('Global__NDI_Name')}: ${nIndex}`);
      }
      const day = JSON.parse(featureProperties.data)?.day;
      if (day) {
        const date = moment(day);
        if (date.isValid()) {
          tooltipLines.push(`${this.translationService.translate('Global__Date')}: ${date.format(this.languageStore.CurrentDateFormat)}`);
        }
      }
      return tooltipLines.join('\n');
    };
  }

  public getNApplicationMapLayerTooltip(): MapLayerTooltipFunction {
    return (featureProperties: { [key: string]: any }, legend: ILegend) => {
      if (!featureProperties) {
        return undefined;
      }
      const rawValue = +JSON.parse(featureProperties.data)?.value;
      let value = this.roundNumericService.roundValue(rawValue);
      const selectedLegendValue = legend.selectionLists[0]?.selectedValue.getValue();
      if (!value || value?.length <= 0) {
        return undefined;
      }
      if (selectedLegendValue.transformFunction) {
        value = this.roundNumericService.round(selectedLegendValue.transformFunction(rawValue), 0);
      }
      if (rawValue === +this.agriportConstantsService.GetConstant('RasterNoValue')) {
        return `${selectedLegendValue.text}: -`;
      }
      return `${selectedLegendValue.text}: ${value} ${selectedLegendValue.suffix ?? ''}`;
    };
  }

  public getNUptakeLayerTooltip(): MapLayerTooltipFunction {
    return (featureProperties: { [key: string]: any }, _: ILegend) => {
      if (!featureProperties) {
        return undefined;
      }
      const tooltipLines = [];
      const nUptake = +JSON.parse(featureProperties.data)?.nuptake;
      const tooltipValue = this.roundNumericService.round((nUptake < 0 ? 0 : nUptake), 0);
      if (tooltipValue && tooltipValue?.length > 0) {
        tooltipLines.push(`${this.translationService.translate('NUptake_Sat')}: ${tooltipValue}`);
      }
      const day = JSON.parse(featureProperties.data)?.day;
      if (day) {
        const date = moment(day);
        if (date.isValid()) {
          tooltipLines.push(`${this.translationService.translate('Global__Date')}: ${date.format(this.languageStore.SelectedLanguage.DateFormat)}`);
        }
      }
      return tooltipLines.join('\n');
    };
  }

  public getPpRasterLayerTooltip(): MapLayerTooltipFunction {
    return (featureProperties: { [key: string]: any }, legend: ILegend) => {
      if (!featureProperties) {
        return undefined;
      }
      const rawValue = +JSON.parse(featureProperties.data)?.value;
      if (!isFinite(rawValue)) {
        return undefined;
      }
      const value = this.roundNumericService.roundValue(rawValue);
      const selectedLegendValue = legend.selectionLists[0]?.selectedValue.getValue();
      const text = this.translationService.translate(selectedLegendValue.text ?? '');
      const suffix = this.translationService.translate(selectedLegendValue.suffix ?? '');
      if (rawValue === +this.agriportConstantsService.GetConstant('RasterNoValue')) {
        return `${selectedLegendValue.text}: -`;
      }
      return `${text}: ${value} ${suffix}`;
    };
  }

  public getPpApplicationMapLayerTooltip(): MapLayerTooltipFunction {
    return (featureProperties: { [key: string]: any }, legend: ILegend) => {
      if (!featureProperties) {
        return undefined;
      }
      const rawValue = +JSON.parse(featureProperties.data)?.value;
      const selectedLegendValue = legend.selectionLists[0]?.selectedValue.getValue();
      const value = this.roundNumericService.roundValue(rawValue);
      if (!value || value?.length <= 0) {
        return undefined;
      }
      if (rawValue === +this.agriportConstantsService.GetConstant('RasterNoValue')) {
        return `${selectedLegendValue.text}: -`;
      }
      return `${this.translationService.translate('Global__Plant_Protection')}: ${value}`;
    };
  }
}
